// API base URL:
// - Production / preview: set VITE_API_URL (e.g. https://api.example.com/api/v1).
// - Local dev (npm run dev): if VITE_API_URL is unset, use `/api/v1` so requests go through Vite’s proxy to Nest
//   (avoids CORS and localhost vs 127.0.0.1 issues). Set VITE_API_URL explicitly for ngrok / remote API.
const envApi = (import.meta.env.VITE_API_URL as string | undefined)?.trim().replace(/\/$/, '');
export const API_URL = envApi || (import.meta.env.DEV ? '/api/v1' : '');
export const WS_URL =
  import.meta.env.VITE_WS_URL || (import.meta.env.DEV ? 'ws://localhost:3000' : '');

// Application Constants
export const APP_NAME = 'Gati Sutra';
export const APP_VERSION = '1.0.0';
export const APP_DESCRIPTION = 'Transportation Management System';

// Pagination
export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Date/Time Formats
export const DATE_FORMAT = 'MMM dd, yyyy';
export const DATETIME_FORMAT = 'MMM dd, yyyy HH:mm';
export const TIME_FORMAT = 'HH:mm';

// Shipment Status
export const SHIPMENT_STATUS = {
  PENDING: 'pending',
  PICKED_UP: 'picked_up',
  IN_TRANSIT: 'in_transit',
  OUT_FOR_DELIVERY: 'out_for_delivery',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
  DELAYED: 'delayed',
  EXCEPTION: 'exception',
} as const;

export const SHIPMENT_STATUS_LABELS = {
  [SHIPMENT_STATUS.PENDING]: 'Pending',
  [SHIPMENT_STATUS.PICKED_UP]: 'Picked Up',
  [SHIPMENT_STATUS.IN_TRANSIT]: 'In Transit',
  [SHIPMENT_STATUS.OUT_FOR_DELIVERY]: 'Out for Delivery',
  [SHIPMENT_STATUS.DELIVERED]: 'Delivered',
  [SHIPMENT_STATUS.CANCELLED]: 'Cancelled',
  [SHIPMENT_STATUS.DELAYED]: 'Delayed',
  [SHIPMENT_STATUS.EXCEPTION]: 'Exception',
} as const;

export const SHIPMENT_STATUS_COLORS = {
  [SHIPMENT_STATUS.PENDING]: 'border border-border bg-card/80 text-foreground',
  [SHIPMENT_STATUS.PICKED_UP]: 'border border-info/35 bg-info/20 text-foreground',
  [SHIPMENT_STATUS.IN_TRANSIT]: 'border border-info/30 bg-info/15 text-foreground',
  [SHIPMENT_STATUS.OUT_FOR_DELIVERY]: 'border border-ring/40 bg-primary/25 text-foreground',
  [SHIPMENT_STATUS.DELIVERED]: 'border border-success/35 bg-success/20 text-foreground',
  [SHIPMENT_STATUS.CANCELLED]: 'border border-destructive/35 bg-destructive/20 text-foreground',
  [SHIPMENT_STATUS.DELAYED]: 'border border-warning/40 bg-warning/15 text-foreground',
  [SHIPMENT_STATUS.EXCEPTION]: 'border border-accent/40 bg-accent/15 text-foreground',
} as const;

// Carrier Status
export const CARRIER_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SUSPENDED: 'suspended',
} as const;

export const CARRIER_STATUS_LABELS = {
  [CARRIER_STATUS.ACTIVE]: 'Active',
  [CARRIER_STATUS.INACTIVE]: 'Inactive',
  [CARRIER_STATUS.SUSPENDED]: 'Suspended',
} as const;

// User Roles
export const USER_ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  DISPATCHER: 'dispatcher',
  DRIVER: 'driver',
  CUSTOMER: 'customer',
} as const;

export const USER_ROLE_LABELS = {
  [USER_ROLES.ADMIN]: 'Administrator',
  [USER_ROLES.MANAGER]: 'Manager',
  [USER_ROLES.DISPATCHER]: 'Dispatcher',
  [USER_ROLES.DRIVER]: 'Driver',
  [USER_ROLES.CUSTOMER]: 'Customer',
} as const;

// Route Status
export const ROUTE_STATUS = {
  PLANNED: 'planned',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
} as const;

// Currency — India-first defaults
export const DEFAULT_CURRENCY = 'INR';
export const CURRENCY_SYMBOL = '₹';

// Distance Units
export const DISTANCE_UNIT = 'km';

// Weight Units
export const WEIGHT_UNIT = 'kg';

// Map Configuration — approximate centre of India
export const DEFAULT_MAP_CENTER = { lat: 22.5937, lng: 78.9629 };
export const DEFAULT_MAP_ZOOM = 5;

// Local Storage Keys
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER_PREFERENCES: 'userPreferences',
  THEME: 'theme',
} as const;

// Toast/Notification Durations (ms)
export const TOAST_DURATION = {
  SUCCESS: 3000,
  ERROR: 5000,
  WARNING: 4000,
  INFO: 3000,
} as const;

// File Upload
export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
export const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/jpg',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;

// Debounce/Throttle Delays (ms)
export const SEARCH_DEBOUNCE_DELAY = 300;
export const AUTOSAVE_DEBOUNCE_DELAY = 1000;

// Analytics Time Ranges
export const TIME_RANGES = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_7_DAYS: 'last_7_days',
  LAST_30_DAYS: 'last_30_days',
  LAST_90_DAYS: 'last_90_days',
  THIS_MONTH: 'this_month',
  LAST_MONTH: 'last_month',
  THIS_YEAR: 'this_year',
  CUSTOM: 'custom',
} as const;
